import {
  nodeOperatorFuncResolver,
  logicalOperatorFuncResolver,
} from './evaluateFunc';
import {
  ExpressionArrayType,
  ExpressionNodeType,
  ExpressionTreeType,
  ExpressionType,
  LogicalOperatorType,
} from './types';

class Expression {
  public expression: ExpressionType;

  constructor(expression: ExpressionType) {
    this.expression = expression;
  }

  //#region public

  public toString = (): string => {
    return this.expressionToString(this.expression, true);
  };

  public evaluate(getAnswer: (dataKey: string) => any) {
    return this.evaluateRecursive(this.expression, getAnswer);
  }

  public toArray = (): ExpressionArrayType => {
    return this.toArrayRecursive(this.expression);
  };

  public hasDataKey = (dataKey: string) =>
    this.toArrayRecursive(this.expression).nodes.some(
      (n) => n.dataKey === dataKey,
    );

  //#endregion

  //#region public static

  public static isExpressionNode = (
    node: ExpressionType,
  ): node is ExpressionNodeType => {
    return 'dataKey' in node;
  };

  public static isExpressionTree = (
    node: ExpressionType,
  ): node is ExpressionTreeType => {
    return 'operandOne' in node;
  };

  //#endregion

  //#region private

  private toArrayRecursive = (
    expression: ExpressionType,
  ): { nodes: ExpressionNodeType[]; operators: LogicalOperatorType[] } => {
    if (Expression.isExpressionNode(expression)) {
      return { nodes: [expression], operators: [] };
    }

    const a1 = this.toArrayRecursive(expression.operandOne);
    const a2 = this.toArrayRecursive(expression.operandTwo);

    return {
      nodes: [...a1.nodes, ...a2.nodes],
      operators: [...a1.operators, expression.operator, ...a2.operators],
    };
  };

  private evaluateRecursive = (
    expression: ExpressionType,
    getAnswer: (dataKey: string) => any,
  ): boolean => {
    if (Expression.isExpressionNode(expression)) {
      return nodeOperatorFuncResolver[expression.operator](
        getAnswer(expression.dataKey),
        expression.value,
      );
    }

    return logicalOperatorFuncResolver[expression.operator](
      this.evaluateRecursive(expression.operandOne, getAnswer),
      this.evaluateRecursive(expression.operandTwo, getAnswer),
    );
  };

  private expressionToString = (
    expression: ExpressionType,
    isRoot?: boolean,
  ): string => {
    const isNode = Expression.isExpressionNode(expression);
    if (isNode) {
      const value =
        expression.value === null ? '' : ` ${JSON.stringify(expression.value)}`;
      const expressionString = `{${expression.dataKey}} ${expression.operator}${value}`;
      return isRoot ? expressionString : `(${expressionString})`;
    }

    const op1 = this.expressionToString(expression.operandOne);
    const op2 = this.expressionToString(expression.operandTwo);
    const expressionString = op1 + ' ' + expression.operator + ' ' + op2;
    return isRoot ? expressionString : `(${expressionString})`;
  };

  //#endregion
}

export default Expression;
